import React from 'react';
import { Alert, Box, Button, Collapse } from '@mui/material';
import { History, ArrowRight } from 'lucide-react';
import { useAcademicYearStore } from '../../store/academicYearStore';
import { useAcademicYear } from '../../hooks/useAcademicYear';
import { useAuthStore } from '../../store/authstore';

/**
 * Banner that displays when viewing data from a non-default (historical) academic year.
 * Shows the current selection and provides a quick way to switch back to the active year.
 */
const HistoricalYearBanner: React.FC = () => {
  const { session } = useAuthStore();
  const instituteId = session?.user?.instituteId ?? 0;

  const {
    selectedAcademicYearId,
    selectedAcademicYearName,
    defaultAcademicYearId,
    isViewingHistorical,
    setSelectedAcademicYear,
  } = useAcademicYearStore();

  const { academicYears } = useAcademicYear(instituteId);

  const handleSwitchToDefault = () => {
    const defaultYear = academicYears.find((y) => y.id === defaultAcademicYearId);
    if (defaultYear) {
      setSelectedAcademicYear(defaultYear.id, defaultYear.name);
    }
  };

  const defaultYearName = academicYears.find((y) => y.id === defaultAcademicYearId)?.name;

  return (
    <Collapse in={isViewingHistorical}>
      <Alert
        severity="warning"
        icon={<History size={20} />}
        sx={{
          borderRadius: 0,
          py: 0.5,
          '& .MuiAlert-message': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          },
        }}
        action={
          <Button
            color="warning"
            size="small"
            endIcon={<ArrowRight size={16} />}
            onClick={handleSwitchToDefault}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Switch to {defaultYearName || 'current year'}
          </Button>
        }
      >
        <Box component="span">
          You are viewing data from <strong>{selectedAcademicYearName}</strong>.
          Some features may be limited for historical years.
        </Box>
      </Alert>
    </Collapse>
  );
};

export default HistoricalYearBanner;
